import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useStyles } from '../../styles';
import '../../styles/runLogsDialog.css';

interface RunLogsDialogProps {
  runId: string;
  fileNames: string[];
  selectedIndices: number[];
  saving: boolean;
  onToggle: (index: number) => void;
  onDownload: (all: boolean) => void;
  onClose: () => void;
}

export function RunLogsDialog({
  runId,
  fileNames,
  selectedIndices,
  saving,
  onToggle,
  onDownload,
  onClose,
}: RunLogsDialogProps) {
  const classes = useStyles();
  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="run-logs-title"
      aria-describedby="run-logs-description"
      PaperProps={{ className: classes.runLogsDialogPaper }}
    >
      <DialogTitle
        id="run-logs-title"
        disableTypography
        className="run-logs-dialog-title"
      >
        <Box>
          <Typography variant="h6">Run logs</Typography>
          <Typography
            id="run-logs-description"
            variant="body2"
            className="run-logs-dialog-run-id"
          >
            Log files for run: {runId}
          </Typography>
        </Box>
        <IconButton
          size="small"
          className="run-logs-dialog-close"
          aria-label="Close run logs"
          onClick={onClose}
          disabled={saving}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="run-logs-dialog-content" aria-busy={saving}>
        <TableContainer className="run-logs-table-container">
          <Table size="small" aria-label="Run log files">
            <TableHead>
              <TableRow>
                <TableCell>File name</TableCell>
                <TableCell align="right">Select</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fileNames.map((fileName, index) => (
                <TableRow
                  key={`${index}-${fileName}`}
                  className="run-logs-table-row"
                >
                  <TableCell className="run-logs-file-name">
                    {fileName}
                  </TableCell>
                  <TableCell align="right">
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={selectedIndices.includes(index)}
                      onChange={() => onToggle(index)}
                      disabled={saving}
                      inputProps={{ 'aria-label': `Select ${fileName}` }}
                      className="run-logs-checkbox"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter className="run-logs-table-footer">
              <TableRow>
                <TableCell colSpan={2}>
                  <Box className="run-logs-footer-actions">
                    <Button
                      variant="text"
                      color="primary"
                      className="run-logs-link-button"
                      disabled={saving || selectedIndices.length === 0}
                      onClick={() => onDownload(false)}
                    >
                      Download Selected
                    </Button>
                    <Button
                      variant="text"
                      color="primary"
                      className="run-logs-link-button"
                      disabled={saving}
                      onClick={() => onDownload(true)}
                    >
                      Download All
                    </Button>
                  </Box>
                  {saving && (
                    <Typography variant="body2" role="status">
                      Preparing download...
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}
